import React, { useRef } from 'react';

// Interactive heal / clone-spot overlay. A spot is {x,y,r,feather,ox,oy} in the
// 0..1 SOURCE-frame space (the heal-mode preview shows the full, un-cropped
// frame, so handles map 1:1 to what the GLSL healedSample() / ffmpeg geq bake).
// `r` is a radius as a fraction of width; the circle is kept round in pixels via
// aspect-ratio:1. The caller owns the spot and persists it; we only clamp.
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

export default function HealBox({ spot, selected, onSelect, onChange }) {
  const ref = useRef(null);
  if (!spot) return null;

  const pt = (e) => {
    const r = ref.current.getBoundingClientRect();
    return {
      x: clamp((e.clientX - r.left) / r.width, 0, 1),
      y: clamp((e.clientY - r.top) / r.height, 0, 1),
    };
  };
  const drag = (e, onMove) => {
    e.preventDefault();
    e.stopPropagation();
    const move = (ev) => onMove(pt(ev));
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const onTargetDown = (e) => {
    onSelect?.();
    const s = pt(e);
    const o = { x: spot.x, y: spot.y };
    drag(e, (p) => onChange({ x: clamp(o.x + (p.x - s.x), 0, 1), y: clamp(o.y + (p.y - s.y), 0, 1) }));
  };
  const onSourceDown = (e) => {
    onSelect?.();
    drag(e, (p) => onChange({ ox: clamp(p.x - spot.x, -1, 1), oy: clamp(p.y - spot.y, -1, 1) }));
  };
  const onRadiusDown = (e) => {
    onSelect?.();
    drag(e, (p) => onChange({ r: clamp(Math.abs(p.x - spot.x), 0.01, 0.5) }));
  };

  const sx = clamp(spot.x + (spot.ox || 0), 0, 1);
  const sy = clamp(spot.y + (spot.oy || 0), 0, 1);
  const dia = `${spot.r * 2 * 100}%`;

  return (
    <div ref={ref} className="cs-healbox-overlay">
      <svg className="cs-healline-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
        <line x1={spot.x * 100} y1={spot.y * 100} x2={sx * 100} y2={sy * 100} className={`cs-healline${selected ? ' selected' : ''}`} />
      </svg>
      {/* source (clone-from) circle */}
      <span
        className={`cs-healcirc source${selected ? ' selected' : ''}`}
        style={{ left: `${sx * 100}%`, top: `${sy * 100}%`, width: dia }}
        onPointerDown={onSourceDown}
        title="Source — clone from here"
      />
      {/* target (blemish) circle */}
      <span
        className={`cs-healcirc target${selected ? ' selected' : ''}`}
        style={{ left: `${spot.x * 100}%`, top: `${spot.y * 100}%`, width: dia }}
        onPointerDown={onTargetDown}
        title="Target — the spot to heal"
      >
        {selected && <span className="cs-healgrip" onPointerDown={onRadiusDown} title="Drag to resize" />}
      </span>
    </div>
  );
}
